import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { SelectField } from '../../../../components/ui/Field';
import type { AiPod } from '../../../../services/aiPodService';
import type { BillingPlanDto } from '../../../../services/billingService';
import {
  aiPodBillingService,
  formatMoney,
  isLiveStatus,
  podStatusLabel,
  type PodSubscription,
} from '../../../../services/aiPodBillingService';

type Tenant = { id: string; name?: string; tenantName?: string };

const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-agentic-text-secondary';
const selectCls = 'w-full rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 text-sm';

function formatDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

/**
 * A pod's subscribers, with super-admin actions: grant a subscription without payment
 * (e.g. migrating an existing client, optionally linking the workflow/chatbot they
 * already run) and cancel a subscription at period end.
 */
const PodSubscribersModal: React.FC<{ pod: AiPod | null; tenants: Tenant[]; onClose: () => void }> = ({ pod, tenants, onClose }) => {
  const [subs, setSubs] = useState<PodSubscription[]>([]);
  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [planId, setPlanId] = useState('');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [linkWorkflowId, setLinkWorkflowId] = useState('');
  const [linkChatbotId, setLinkChatbotId] = useState('');
  const [linkable, setLinkable] = useState<{ workflows: any[]; chatbots: any[] }>({ workflows: [], chatbots: [] });
  const [busy, setBusy] = useState<string | null>(null);

  const tenantName = (id: string) => {
    const t = tenants.find((x) => x.id === id);
    return t?.name ?? t?.tenantName ?? id;
  };

  const load = async () => {
    if (!pod) return;
    setLoading(true);
    try {
      const [list, planRes] = await Promise.all([
        aiPodBillingService.listSubscribers(pod.id),
        aiPodBillingService.listBillingPlans(pod.id),
      ]);
      setSubs(list);
      setPlans(planRes.plans ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setGrantOpen(false);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pod?.id]);

  useEffect(() => {
    setLinkWorkflowId('');
    setLinkChatbotId('');
    if (!tenantId) return setLinkable({ workflows: [], chatbots: [] });
    aiPodBillingService.linkableResources(tenantId).then(setLinkable).catch(() => setLinkable({ workflows: [], chatbots: [] }));
  }, [tenantId]);

  const selectedPlan = plans.find((p) => p.id === planId);
  const intervals = useMemo(() => {
    if (!selectedPlan) return [] as Array<'month' | 'year'>;
    const set = new Set<string>([selectedPlan.interval ?? 'month', ...(selectedPlan.pricingIntervals ?? []).map((r) => r.interval)]);
    return (['month', 'year'] as const).filter((i) => set.has(i));
  }, [selectedPlan]);

  useEffect(() => {
    if (intervals.length && !intervals.includes(interval)) setInterval(intervals[0]);
  }, [intervals, interval]);

  const liveTenantIds = new Set(subs.filter((s) => isLiveStatus(s.status)).map((s) => s.tenantId));

  const grant = async () => {
    if (!tenantId || !planId) return toast.error('Choose a tenant and a plan');
    setBusy('grant');
    try {
      await aiPodBillingService.grantSubscription({
        tenantId,
        planId,
        interval,
        ...(linkWorkflowId ? { linkWorkflowId } : {}),
        ...(linkChatbotId ? { linkChatbotId } : {}),
      });
      toast.success(`Subscription granted to ${tenantName(tenantId)}`);
      setGrantOpen(false);
      setTenantId('');
      setPlanId('');
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to grant subscription');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (sub: PodSubscription) => {
    if (!window.confirm(`Cancel ${tenantName(sub.tenantId)}'s subscription at the end of the current period?`)) return;
    setBusy(sub.id);
    try {
      await aiPodBillingService.adminCancelSubscription(sub.id);
      toast.success('Subscription will end at the end of the period');
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to cancel');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal isOpen={!!pod} onClose={onClose} title={pod ? `${pod.name} — subscribers` : ''} size="xl">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-agentic-text-secondary">{subs.length} subscription(s)</p>
          <Button variant="primary" size="sm" onClick={() => setGrantOpen((v) => !v)}>
            {grantOpen ? 'Close' : 'Grant subscription'}
          </Button>
        </div>

        {grantOpen && (
          <div className="space-y-3 rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
            <p className="text-xs text-agentic-text-muted">
              Gives a tenant this pod without payment. It renews each billing period until cancelled. To migrate a client that already runs
              a workflow or chatbot, link it instead of creating new ones.
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className={labelCls}>Tenant</label>
                <SelectField className={selectCls} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                  <option value="">Select tenant…</option>
                  {tenants
                    .filter((t) => !liveTenantIds.has(t.id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name ?? t.tenantName ?? t.id}
                      </option>
                    ))}
                </SelectField>
              </div>
              <div>
                <label className={labelCls}>Plan</label>
                <SelectField className={selectCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
                  <option value="">Select plan…</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div>
                <label className={labelCls}>Billing period</label>
                <SelectField className={selectCls} value={interval} onChange={(e) => setInterval(e.target.value as 'month' | 'year')} disabled={!selectedPlan}>
                  {intervals.map((i) => (
                    <option key={i} value={i}>
                      {i === 'month' ? 'Monthly' : 'Yearly'}
                    </option>
                  ))}
                </SelectField>
              </div>
              {pod?.channel !== 'chat' && (
                <div className="md:col-span-1">
                  <label className={labelCls}>Link existing workflow (optional)</label>
                  <SelectField className={selectCls} value={linkWorkflowId} onChange={(e) => setLinkWorkflowId(e.target.value)} disabled={!tenantId}>
                    <option value="">Create a new one</option>
                    {linkable.workflows.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} ({w.status})
                      </option>
                    ))}
                  </SelectField>
                </div>
              )}
              {pod?.channel !== 'workflow' && (
                <div className="md:col-span-1">
                  <label className={labelCls}>Link existing chatbot (optional)</label>
                  <SelectField className={selectCls} value={linkChatbotId} onChange={(e) => setLinkChatbotId(e.target.value)} disabled={!tenantId}>
                    <option value="">Create a new one</option>
                    {linkable.chatbots.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SelectField>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="primary" size="sm" loading={busy === 'grant'} onClick={grant}>
                Grant
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="py-8 text-center text-sm text-agentic-text-muted">Loading…</p>
        ) : subs.length === 0 ? (
          <p className="py-8 text-center text-sm text-agentic-text-muted">No tenant has subscribed to this pod yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-agentic-gray-200 text-left text-xs uppercase tracking-wide text-agentic-text-muted">
                  <th className="py-2 pr-3">Tenant</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Period ends</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-agentic-gray-100">
                {subs.map((s) => (
                  <tr key={s.id}>
                    <td className="py-2 pr-3 font-medium text-agentic-text-primary">{tenantName(s.tenantId)}</td>
                    <td className="py-2 pr-3">
                      {s.snapshot?.plan?.displayName ?? '—'}
                      {/* A free plan already called "Free" needs no price suffix. */}
                      {(s.snapshot?.plan?.amountMinor || s.snapshot?.plan?.displayName?.toLowerCase() !== 'free') && (
                        <span className="text-agentic-text-muted">
                          {' '}
                          · {s.snapshot?.plan?.amountMinor ? `${formatMoney(s.snapshot.plan.amountMinor, s.snapshot.plan.currency)}/${s.snapshot.plan.interval}` : 'Free'}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 capitalize">{s.source}</td>
                    <td className="py-2 pr-3">{podStatusLabel(s)}</td>
                    <td className="py-2 pr-3">{formatDate(s.status === 'canceled' ? s.endedAt : s.currentPeriodEnd)}</td>
                    <td className="py-2 text-right">
                      {isLiveStatus(s.status) && !s.cancelAtPeriodEnd && (
                        <Button variant="ghost" size="sm" loading={busy === s.id} onClick={() => cancel(s)}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PodSubscribersModal;
